<template>
  <Modal ref="modalRef" width="520px">
    <template #header>{{ scope ? `Download ${scope.name} leads` : 'Download portfolio inquiries' }}</template>

    <template #body>
      <View direction="column" gap="md" class="form">
        <FormField label="Choose period">
          <Select v-model="selectedFilter">
            <option v-for="filter in timeFilters" :key="filter" :value="filter">{{ filter }}</option>
          </Select>
        </FormField>

        <!-- Only once the page is narrowed: the file follows what is on screen, and says so -->
        <Hint v-if="scope" icon="info-circle">{{ scopeHint }}</Hint>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isDownloading" :disabled="isDownloading || !companyStore.company?.id" @click="download">
        Download
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref, computed } from 'vue';

import { Button, FormField, Hint, Modal, Select, View, useAlertsStore } from '@tallo/design-system';
import { useCompanyStore } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { propertyStatisticsService, unassignedPortfolioFilter } from '@tallo/investor/property';
import { TimeFilter, timeFilters, downloadFile, generateStatsFilename } from '@tallo/utility';

/** What the properties page is narrowed to: a portfolio's id, or `unassigned`, with the name it shows */
interface DownloadScope {
  portfolio: string;
  name: string;
}

const props = defineProps<{ scope?: DownloadScope }>();

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const companyId = computed(() => companyStore.company?.id);
const isDownloading = ref(false);
const selectedFilter = ref<TimeFilter>(TimeFilter.LAST_7_DAYS);
const alertsStore = useAlertsStore();
const companyStore = useCompanyStore();
const { isAdmin } = storeToRefs(useInvestorStore());

// The export covers the units the seat works, which for an admin is every unit
const scopeHint = computed(() => {
  const scope = props.scope;

  if (!scope) {
    return '';
  }

  const loose = scope.portfolio === unassignedPortfolioFilter;
  const units = isAdmin.value
    ? loose
      ? 'the properties in no portfolio'
      : `every property in ${scope.name}`
    : loose
      ? 'the properties you work that are in no portfolio'
      : `the properties you work in ${scope.name}`;

  return `The file lists the leads that came in during this period for ${units}.`;
});

async function open() {
  // Ensure company is loaded before interacting with the modal
  if (!companyStore.isLoaded && !companyStore.isLoading) {
    try {
      await companyStore.load();
    } catch (e) {
      console.error('Failed to load company for download modal:', e);
    }
  }
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

async function download() {
  try {
    isDownloading.value = true;

    if (!companyId.value) {
      alertsStore.error('Company not loaded');
      return;
    }

    const blob = await propertyStatisticsService.downloadCompanyStatistics(
      companyId.value,
      selectedFilter.value,
      props.scope?.portfolio,
    );
    const filename = generateStatsFilename(props.scope ? `${props.scope.name} leads` : 'leads', selectedFilter.value);
    downloadFile(blob, filename);
    alertsStore.success('Download started');
    close();
  } catch (e) {
    console.error(e);
    alertsStore.error('Failed to download. Please try again.');
  } finally {
    isDownloading.value = false;
  }
}

defineExpose({ open, close, modalRef });
</script>

<style scoped lang="scss">
.form {
  width: 100%;
}
</style>
